import { useContext, useEffect, useState } from 'react';
import { DashboardLayout } from '@dealstack/frontend/shared/components/DashboardLayout/DashboardLayout';
import { Flex } from '@mantine/core';
import {
	useTranslationAdminDashboard,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DealSummary } from '@dealstack/frontend/app/pages/Project/DealDetails/DealSummary.tsx';
import { InviteTeamMemberDrawer } from '@dealstack/frontend/shared/components/InviteTeamMemberDrawer/InviteTeamMemberDrawer';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { makeDealPageBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/dealBreadcrumbs.utils';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier.tsx';
import {
	getResourceTypePermissions,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { useInviteDealUser } from '@dealstack/frontend/app/pages/Project/DealDetails/hooks';
import { DealDetailsContent } from '@dealstack/frontend/app/pages/Project/DealDetails/DealDetailsContent.tsx';

export const DealDetails = () => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const tCommonUi = useTranslationCommonUi();
	const {
		deal,
		getDealTeamRequest,
		team,
		deals,
		dealId,
		createMipLoading,
		createNewMip,
		openQuickCreate,
		onIPAccess,
		refetchDealData,
		isRefreshingPermissions,
	} = useContext(DealContext);
	const [showInviteDrawer, setShowInviteDrawer] = useState(false);
	const { mutate: inviteDealUser, isPending } = useInviteDealUser();

	useBreadcrumbs(
		makeDealPageBreadcrumbs({
			tCommonUi,
			dealId,
			dealName: deal?.name,
			deals,
		}),
		{ skipFirst: true }
	);

	useEffect(() => {
		if (refetchDealData) {
			void refetchDealData();
		}
	}, []);

	const dealPermissions = getResourceTypePermissions(ResourceType.DEAL);

	const permissions = dealPermissions.map((relation) => ({
		label: tAdminDashboard(`invite_team_member.relation.${relation}`),
		value: relation,
	}));

	const handleDealInvite = ({ firstName, lastName, email, role }, { onSuccess }) => {
		if (!dealId) {
			throw Error('Expect dealId to be defined');
		}
		inviteDealUser(
			{
				dealId,
				firstName,
				lastName,
				email,
				permissionRoles: [role],
			},
			{
				onSuccess: async () => {
					await getDealTeamRequest.refetch();
					onSuccess?.();
				},
			}
		);
	};

	return (
		<DashboardLayout
			title={deal?.name}
			titleDataTestId={TEST_ELEMENT_IDS.ADMIN_DEAL_PAGE_DEAL_NAME}
			sidebar={
				<Flex align="start" h="100%" w="100%">
					<DealSummary
						targetName={deal?.targetName}
						currency={deal?.currency || ''}
						users={team || []}
						onAddMemberClick={() => setShowInviteDrawer(true)}
						dealId={dealId}
					/>
				</Flex>
			}
		>
			<DealDetailsContent
				dealId={dealId}
				createMipLoading={createMipLoading}
				createNewMip={createNewMip}
				openQuickCreate={openQuickCreate}
				onIPAccess={onIPAccess}
				mips={deal?.mips ?? []}
				isRefreshingPermissions={isRefreshingPermissions}
			/>
			{deal?.id && (
				<InviteTeamMemberDrawer
					isOpen={showInviteDrawer}
					setIsOpen={setShowInviteDrawer}
					isLoading={isPending}
					onInvite={handleDealInvite}
					permissions={permissions}
					description={tAdminDashboard('invite_member_drawer.description')}
					roleLabel={tAdminDashboard('invite_member_drawer.form.role_field.label')}
				/>
			)}
		</DashboardLayout>
	);
};
